import { FileDownload, KeyboardArrowDown, KeyboardArrowUp, Refresh, Terminal } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';

import { fontFamilyMonospace } from 'layout/theme';
import {
  DangerLevelBadge,
  ExecutionStatusBadge,
  TerminalFeedback,
  getExecutionStatusConfig,
} from '../../../components/commands';
import { CommandExecution, EXECUTION_STATUSES, ExecutionFilters } from '../../../types/commands';

interface ExecutionHistoryProps {
  unitId: number;
}

/**
 * Execution history table with filtering and pagination
 *
 * Features:
 * - Filter by status
 * - Filter by command
 * - Pagination
 * - Expandable rows for details
 * - Auto-refresh toggle
 * - Export to CSV
 */
export const ExecutionHistory: React.FC<ExecutionHistoryProps> = ({ unitId }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // State
  const [executions, setExecutions] = useState<CommandExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Terminal feedback state
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState<CommandExecution | null>(null);

  // Filters
  const [filters, setFilters] = useState<ExecutionFilters>({
    page: 1,
    per_page: 15,
  });

  // Load executions
  const loadExecutions = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, total: totalCount } = await dataProvider.getCommandExecutions(unitId, {
        ...filters,
        page: page + 1, // API uses 1-based pagination
        per_page: rowsPerPage,
      });
      setExecutions(data);
      setTotal(totalCount);
    } catch (err: any) {
      setError(err.message || 'Failed to load execution history');
      notify('Failed to load execution history', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Load on mount and when filters/pagination change
  useEffect(() => {
    loadExecutions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId, page, rowsPerPage, filters]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadExecutions();
    }, 10000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, unitId, page, rowsPerPage, filters]);

  // Handle filter changes
  const handleFilterChange = (key: keyof ExecutionFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
    setPage(0); // Reset to first page
  };

  // Handle row expand
  const handleRowClick = (executionId: number) => {
    setExpandedRow(expandedRow === executionId ? null : executionId);
  };

  // Check if execution can show terminal (all executions can show terminal)
  const canShowTerminal = (execution: CommandExecution): boolean => {
    return true;
  };

  // Get terminal button configuration based on execution status
  const getTerminalButtonConfig = (execution: CommandExecution) => {
    switch (execution.status) {
      case 'completed':
        return {
          text: 'Show Terminal',
          color: 'success' as const,
          variant: 'contained' as const,
        };
      case 'failed':
      case 'timeout':
        return {
          text: 'Show Terminal',
          color: 'error' as const,
          variant: 'contained' as const,
        };
      case 'pending':
      case 'queued':
      case 'sent':
      case 'acknowledged':
        return {
          text: 'Reconnect to Terminal',
          color: 'warning' as const,
          variant: 'outlined' as const,
        };
      default:
        return {
          text: 'Show Terminal',
          color: 'primary' as const,
          variant: 'outlined' as const,
        };
    }
  };

  // Handle terminal reconnect
  const handleTerminalReconnect = (execution: CommandExecution) => {
    setSelectedExecution(execution);
    setTerminalOpen(true);
  };

  // Handle terminal close
  const handleTerminalClose = () => {
    setTerminalOpen(false);
    setSelectedExecution(null);
    // Refresh executions to get updated status
    loadExecutions();
  };

  // Export to CSV
  const handleExport = () => {
    const csv = [
      // Header
      ['ID', 'UUID', 'Command', 'Status', 'User', 'Created At', 'Completed At', 'Error'].join(','),
      // Rows
      ...executions.map((exec) =>
        [
          exec.id,
          exec.uuid,
          exec.command.name,
          exec.status,
          exec.user.email,
          exec.created_at,
          exec.completed_at || '',
          exec.error_message || '',
        ].join(','),
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `command-executions-unit-${unitId}-${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading && executions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="execution-status-filter-label">Status</InputLabel>
              <Select
                labelId="execution-status-filter-label"
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                {EXECUTION_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {getExecutionStatusConfig(status).label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Command ID"
              value={filters.command_id || ''}
              onChange={(e) => handleFilterChange('command_id', parseInt(e.target.value) || undefined)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<Refresh />} onClick={() => loadExecutions()} size="small">
                Refresh
              </Button>

              <Button
                variant={autoRefresh ? 'contained' : 'outlined'}
                onClick={() => setAutoRefresh(!autoRefresh)}
                size="small"
              >
                {autoRefresh ? 'Auto ✓' : 'Auto'}
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExport}
              size="small"
              fullWidth
              disabled={executions.length === 0}
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>ID</TableCell>
              <TableCell>Command</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {executions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No executions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              executions.map((execution) => (
                <React.Fragment key={execution.id}>
                  {/* Main Row */}
                  <TableRow
                    hover
                    onClick={() => handleRowClick(execution.id)}
                    sx={{ 'cursor': 'pointer', '& > *': { borderBottom: 'unset' } }}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {expandedRow === execution.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </TableCell>
                    <TableCell>#{execution.id}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{execution.command.name}</Typography>
                        <DangerLevelBadge level={execution.command.danger_level} size="small" />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <ExecutionStatusBadge status={execution.status} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{execution.user.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {execution.user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{new Date(execution.created_at).toLocaleString()}</Typography>
                    </TableCell>
                    <TableCell>
                      {execution.completed_at ? (
                        <Typography variant="body2">
                          {Math.round(
                            (new Date(execution.completed_at).getTime() - new Date(execution.created_at).getTime()) /
                              1000,
                          )}
                          s
                        </Typography>
                      ) : execution.status === 'timeout' ? (
                        <Typography variant="body2" color="error">
                          Timed out
                        </Typography>
                      ) : execution.status === 'queued' ? (
                        <Typography variant="body2" color="text.secondary">
                          Waiting for unit
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          In progress
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row */}
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                      <Collapse in={expandedRow === execution.id} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Execution Details</Typography>
                            {canShowTerminal(execution) && (
                              <Button
                                variant={getTerminalButtonConfig(execution).variant}
                                size="small"
                                startIcon={<Terminal />}
                                onClick={() => handleTerminalReconnect(execution)}
                                color={getTerminalButtonConfig(execution).color}
                              >
                                {getTerminalButtonConfig(execution).text}
                              </Button>
                            )}
                          </Box>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2">UUID:</Typography>
                              <Typography variant="body2" color="text.secondary" fontFamily={fontFamilyMonospace}>
                                {execution.uuid}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2">Command:</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {execution.command.description}
                              </Typography>
                            </Grid>

                            <Grid item xs={12}>
                              <Typography variant="subtitle2" gutterBottom>
                                Parameters:
                              </Typography>
                              <Box
                                component="pre"
                                sx={{
                                  backgroundColor: 'grey.100',
                                  padding: 1,
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                  overflow: 'auto',
                                }}
                              >
                                {JSON.stringify(execution.parameters, null, 2)}
                              </Box>
                            </Grid>

                            {execution.result && (
                              <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Result:
                                </Typography>
                                <Box
                                  component="pre"
                                  sx={{
                                    backgroundColor: 'grey.100',
                                    padding: 1,
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    overflow: 'auto',
                                  }}
                                >
                                  {JSON.stringify(execution.result, null, 2)}
                                </Box>
                              </Grid>
                            )}

                            {execution.error_message && (
                              <Grid item xs={12}>
                                <Alert severity="error">
                                  <strong>Error:</strong> {execution.error_message}
                                </Alert>
                              </Grid>
                            )}

                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary">
                                Sent: {execution.sent_at ? new Date(execution.sent_at).toLocaleString() : 'N/A'}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary">
                                Acknowledged:{' '}
                                {execution.acknowledged_at
                                  ? new Date(execution.acknowledged_at).toLocaleString()
                                  : ['acknowledged', 'completed', 'failed'].includes(execution.status)
                                    ? 'Yes'
                                    : ['sent', 'queued'].includes(execution.status)
                                      ? 'Pending'
                                      : 'N/A'}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary">
                                Completed:{' '}
                                {execution.completed_at ? new Date(execution.completed_at).toLocaleString() : 'N/A'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 15, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Terminal Feedback Dialog */}
      {selectedExecution && (
        <TerminalFeedback
          open={terminalOpen}
          onClose={handleTerminalClose}
          hostname={selectedExecution.unit.hostname}
          executionUuid={selectedExecution.uuid}
          executionId={selectedExecution.id}
          commandName={selectedExecution.command.name}
          commandSlug={selectedExecution.command.name}
          feedbackConfig={undefined} // feedback_config not available in CommandReference
        />
      )}
    </Box>
  );
};

export default ExecutionHistory;
